import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { submitContractor, track } from "../../lib/api";
import { BUSINESS, SERVICE_AREAS } from "../../data/site";

const STEP_LABELS = ["Your Business", "Your Dumpster Needs", "Contact"];
const SITES = ["1", "2–3", "4+"];
const FREQUENCY = ["Ongoing / Long-Term", "Multiple Times Per Week", "Weekly", "As Needed Per Project"];
const SWAPS = ["Rarely", "About Once a Week", "Multiple Times Per Week"];

function OptionCard({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      data-testid={testid}
      className={`px-4 py-3 rounded-lg border-2 text-left font-heading font-semibold text-sm transition-all active:scale-[0.98] ${
        selected ? "border-sky-600 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
      }`}
    >
      {children}
    </button>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-lg border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-sky-600 focus:outline-none transition-colors";

export default function ContractorForm() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company: "",
    name: "",
    serviceArea: "",
    activeSites: "",
    frequency: "",
    swapFrequency: "",
    phone: "",
    email: "",
    nextNeeded: "",
    notes: "",
    website: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.company.trim()) return "Company name is required.";
      if (!form.name.trim()) return "Please enter your name.";
      if (!form.serviceArea) return "Please choose your primary service area.";
    }
    if (step === 1 && (!form.activeSites || !form.frequency || !form.swapFrequency))
      return "Please answer all three questions — it helps us price around your usage.";
    if (step === 2) {
      if (!form.phone.trim()) return "Please enter your phone number.";
      if (!/.+@.+\..+/.test(form.email)) return "Please enter a valid email address.";
    }
    return "";
  };

  const next = () => {
    const err = validateStep();
    if (err) return setError(err);
    setError("");
    setStep((s) => s + 1);
  };

  const submit = async () => {
    const err = validateStep();
    if (err) return setError(err);
    setError("");
    setSubmitting(true);
    try {
      await submitContractor({
        company: form.company,
        name: form.name,
        service_area: form.serviceArea,
        active_sites: form.activeSites,
        frequency: form.frequency,
        swap_frequency: form.swapFrequency,
        phone: form.phone,
        email: form.email,
        next_needed: form.nextNeeded,
        notes: form.notes,
        website: form.website,
      });
      track("contractor_pricing_submit", { company: form.company });
      setDone(true);
    } catch {
      setError("Something went wrong. Please call us at (928) 713-2710.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 text-center" data-testid="contractor-success">
        <CheckCircle2 className="h-14 w-14 text-sky-600 mx-auto mb-4" />
        <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">We Got It, {form.company}</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          Nathaniel or Kay will reach out during business hours (Mon–Fri, 8 AM–5 PM) to talk through pricing built around how {form.company} actually uses us.
        </p>
        <p className="mt-5 text-sm text-slate-500">
          Need a dumpster sooner? <a href={BUSINESS.phoneHref} className="font-semibold text-sky-700" data-testid="contractor-success-call">Call {BUSINESS.phone}</a>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="contractor-form">
      <div className="px-6 pt-6 pb-4 border-b border-slate-100">
        <h3 className="font-heading font-bold text-xl text-slate-900">Get Contractor Pricing</h3>
        <p className="text-sm text-slate-500 mt-1">Pricing built around how you actually use us. Two minutes, no interrogation.</p>
        <div className="flex items-center gap-2 mt-4" aria-label={`Step ${step + 1} of 3: ${STEP_LABELS[step]}`}>
          {STEP_LABELS.map((l, i) => (
            <div key={l} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-sky-600" : "bg-slate-200"}`} />
              <span className={`block mt-1.5 text-[11px] font-medium ${i === step ? "text-sky-700" : "text-slate-400"}`}>{l}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
            {step === 0 && (
              <fieldset className="space-y-4">
                <div>
                  <label htmlFor="cf-company" className="block font-heading font-semibold text-slate-900 mb-2">
                    Company Name <span className="text-sky-700">*</span>
                  </label>
                  <input id="cf-company" autoComplete="organization" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="cf-company" />
                </div>
                <div>
                  <label htmlFor="cf-name" className="block font-heading font-semibold text-slate-900 mb-2">Your Name</label>
                  <input id="cf-name" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="cf-name" />
                </div>
                <div>
                  <label htmlFor="cf-area" className="block font-heading font-semibold text-slate-900 mb-2">Primary Service Area</label>
                  <select id="cf-area" value={form.serviceArea} onChange={(e) => set("serviceArea", e.target.value)} className={inputCls} data-testid="cf-area">
                    <option value="">Select an area…</option>
                    {SERVICE_AREAS.map((a) => (
                      <option key={a.slug} value={a.name}>{a.name}</option>
                    ))}
                    <option value="Multiple / Varies">Multiple / Varies</option>
                  </select>
                </div>
              </fieldset>
            )}

            {step === 1 && (
              <fieldset className="space-y-5">
                <div>
                  <span className="block font-heading font-semibold text-slate-900 mb-2">Typical active job sites</span>
                  <div className="grid grid-cols-3 gap-2.5">
                    {SITES.map((s) => (
                      <OptionCard key={s} selected={form.activeSites === s} onClick={() => set("activeSites", s)} testid={`cf-sites-${s.replace(/[^a-z0-9]+/gi, "-")}`}>{s}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="block font-heading font-semibold text-slate-900 mb-2">How often do you need dumpsters?</span>
                  <div className="grid grid-cols-2 gap-2.5">
                    {FREQUENCY.map((f) => (
                      <OptionCard key={f} selected={form.frequency === f} onClick={() => set("frequency", f)} testid={`cf-freq-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{f}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="block font-heading font-semibold text-slate-900 mb-2">Typical swap frequency</span>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {SWAPS.map((s) => (
                      <OptionCard key={s} selected={form.swapFrequency === s} onClick={() => set("swapFrequency", s)} testid={`cf-swap-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</OptionCard>
                    ))}
                  </div>
                </div>
              </fieldset>
            )}

            {step === 2 && (
              <fieldset className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="cf-phone" className="block font-heading font-semibold text-slate-900 mb-2">Phone</label>
                    <input id="cf-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="cf-phone" />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="block font-heading font-semibold text-slate-900 mb-2">Email</label>
                    <input id="cf-email" type="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="cf-email" />
                  </div>
                </div>
                <div>
                  <label htmlFor="cf-next" className="block font-heading font-semibold text-slate-900 mb-2">When is your next dumpster needed?</label>
                  <input id="cf-next" value={form.nextNeeded} onChange={(e) => set("nextNeeded", e.target.value)} className={inputCls} data-testid="cf-next" placeholder="e.g. This week, next Monday…" />
                </div>
                <div>
                  <label htmlFor="cf-notes" className="block font-heading font-semibold text-slate-900 mb-2">
                    Notes <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <textarea id="cf-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} data-testid="cf-notes" />
                </div>
                <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute opacity-0 h-0 w-0 pointer-events-none" />
              </fieldset>
            )}
          </motion.div>
        </AnimatePresence>

        {error && (
          <p role="alert" className="mt-4 text-sm font-medium text-slate-800 bg-slate-100 border border-slate-300 rounded-lg px-4 py-2.5" data-testid="cf-error">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between gap-3 mt-6">
          {step > 0 ? (
            <button type="button" onClick={() => { setError(""); setStep((s) => s - 1); }} className="inline-flex items-center gap-1.5 px-4 py-3 rounded-lg text-slate-600 font-heading font-semibold text-sm hover:bg-slate-100 transition-colors" data-testid="cf-back">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <span />
          )}
          {step < 2 ? (
            <button type="button" onClick={next} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-heading font-semibold transition-all active:scale-[0.98]" data-testid="cf-continue">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white font-heading font-semibold transition-all active:scale-[0.98]" data-testid="cf-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Get Contractor Pricing
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
